import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Stack, Pagination, Select, MenuItem, Tooltip, Skeleton } from '@mui/material';
import moment from 'moment';
import useDebounce from '@Hooks/useDebounce';
import {
    FileDown,
    Plus,
    UserMinus,
    Edit3,
    Users,
    RotateCw,
    Search,
    Clock,
    TrendingUp,
    UserPlus,
    Eye,
    CheckCircle,
    XCircle,
    Sparkles,
    PhoneCall,
    PauseCircle,
    Ban,
    SlidersHorizontal,
    ChevronDown,
    RefreshCw
} from 'lucide-react';

import SnackbarManager from '@CommonFunctionalDisplay/GlobalSnackbar/SnackbarManager';
import CreateLeadsForm from '@FormForms/CreateLeadsForm';
import UserAvatar from '@CommonRepresentationalDisplay/UserAvatar';
import FullUserAvatar from '@CommonRepresentationalDisplay/FullUserAvatar';
import LeadsView from '@ComponentViews/LeadsView';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import ExportDownloadForm from '@FormForms/ExportDownloadForm';
import LeadsService from '@Helpers/URLHelpers/LeadsService';
import LeadsFilters from '@CommonFunctionalDisplay/LeadsFilters';
import WhatsappPhoneNumberService from '@Helpers/URLHelpers/WeboutService/WhatsappPhoneNumberService';
import ErrorHandler from '@Root/ErrorHandler';
import './LeadsPage.scss';

const DEFAULT_DOWNLOAD_OPTIONS = [
    {
        id: 0,
        field: 'name',
        description: 'Full name of the user.',
        display: 'User Name',
        customField: false
    },
    {
        id: 1,
        field: 'email',
        description: 'Email address of the user.',
        display: 'Email',
        customField: false
    },
    {
        id: 2,
        field: 'contactNumber',
        description: 'Phone number of the user.',
        display: 'Phone Number',
        customField: false
    },
    {
        id: 3,
        field: 'companyName',
        description: 'Company name of the user.',
        display: 'Company Name',
        customField: false
    },
    {
        id: 5,
        field: 'status',
        description: 'Message delivery status.',
        display: 'Status',
        customField: false
    },
    {
        id: 6,
        field: 'priority',
        description: 'Priority of the Item.',
        display: 'Priority',
        customField: false
    },
    {
        id: 7,
        field: 'query',
        description: 'Query.',
        display: 'Query',
        customField: false
    },
    {
        id: 8,
        field: 'source',
        description: 'Source.',
        display: 'Source',
        customField: false
    },
    {
        id: 9,
        field: 'address',
        description: 'Address of the user.',
        display: 'Address',
        customField: false
    },
    {
        id: 10,
        field: 'statusUpdate',
        description: 'The Pinned Comment.',
        display: 'Pinned Comment',
        customField: false
    },
    {
        id: 11,
        field: 'comments',
        description: 'Comments.',
        display: 'Comments',
        customField: false
    },
    {
        id: 12,
        field: 'reporterFullName',
        description: 'Reporter full name.',
        display: 'Reporter Full Name',
        customField: false
    },
    {
        id: 13,
        field: 'reporterUserName',
        description: 'Reporter username.',
        display: 'Reporter User Name',
        customField: false
    },
    {
        id: 14,
        field: 'assigneeFullName',
        description: 'Assignee full name.',
        display: 'Assignee Full Name',
        customField: false
    },
    {
        id: 15,
        field: 'assigneeUserName',
        description: 'Assignee username.',
        display: 'Assignee User Name',
        customField: false
    },
    {
        id: 16,
        field: 'createTimeStamp',
        description: 'Leads Created At',
        display: 'Created At',
        customField: false
    }
];

const LEAD_STATUSES = [
    { key: 'newLeads', label: 'New Leads' },
    { key: 'contacted', label: 'Contacted' },
    { key: 'followUp', label: 'Follow Up' },
    { key: 'active', label: 'Active' },
    { key: 'onHold', label: 'On Hold' },
    { key: 'onboarded', label: 'Onboarded' },
    { key: 'closed', label: 'Closed' },
    { key: 'lost', label: 'Lost' }
];

function LeadsPage() {
    const [cursor, setCursor] = useState('auto');
    const [loading, setLoading] = useState(false);

    const [leads, setLeads] = useState([]);
    const [openLeadView, setOpenLeadView] = useState(false);
    const [selectedLead, setSelectedLead] = useState({});

    const [searchQuery, setSearchQuery] = useState('');
    const [showLeadsDownloadForm, setShowLeadsDownloadForm] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);

    const { currentProjectId } = useReduxProject();
    const { activeUserId } = useReduxActiveUser();

    const [defaultFilter] = useState({
        leadsFilter: {
            projectIdList: [currentProjectId],
            statusList: [],
            activeList: [true],
            sourceList: [],
            sourceTargetIdList: [],
            sortBy: 'createTimeStamp',
            sortAsc: false
        },
        page: 1,
        size: 10
    });

    const [leadFilter, setLeadFilter] = useState({
        ...defaultFilter
    });

    const [whatsappPhoneNumberList, setWhatsappPhoneNumberList] = useState([]);
    const [page, setPage] = useState(1);
    const [size] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalResult, setTotalResult] = useState(0);

    const debouncedSearch = useDebounce(searchQuery, 500, true);

    const leadsListRef = useRef(null);

    const leadsService = new LeadsService();
    const whatsappPhoneNumberService = new WhatsappPhoneNumberService();
    const errorHandler = new ErrorHandler();

    const className = 'leads-page';

    useEffect(()=> {
        fetchWhatsappPhoneNumbers();
    }, []);

    useEffect(() => {
        fetchLeadsData(page - 1, size);
    }, [page, size, debouncedSearch, leadFilter]);

    const buildListFilter = useCallback(
        (pageNumber = page - 1, pageSize = size) => ({
            leadsFilter: {
                ...leadFilter.leadsFilter,
                searchQuery: debouncedSearch,
                projectIdList: [currentProjectId]
            },
            page: pageNumber,
            size: pageSize
        }),
        [
            leadFilter,
            currentProjectId,
            page,
            size,
            debouncedSearch
        ]
    );

    const safeJsonParse = (value, fallback = null) => {
        try {
            if (typeof value === 'string') {
                return JSON.parse(value);
            }

            return value;
        } catch (e) {
            return fallback;
        }
    };

    const parseLeadsData = (item) => ({
        ...item,
        details: safeJsonParse(item.details, {}),
        history: safeJsonParse(item.history, []),
        comments: safeJsonParse(item.comments, [])
    });

    const fetchWhatsappPhoneNumbers = async ()=> {
        try {
            let response = await whatsappPhoneNumberService.getPhoneNumberListByProjectId(currentProjectId);
            if (response.status < 400) {
                response = JSON.parse(response.body);
                setWhatsappPhoneNumberList(response);
            }
        } catch(err) {
            errorHandler.handleError(err);
        }
    }

    const fetchLeadsData = async (apiPage = page - 1, pageSize = size) => {
        setLoading(true);
        try {
            apiPage = searchQuery !== '' ? 0 : apiPage;
            const body = buildListFilter(apiPage, pageSize);
            let response = await leadsService.getFilteredList(body);
            if (response.status < 400) {
                response = JSON.parse(response.body);
                const leadsList = response.leadsList.map(parseLeadsData);
                setLeads(leadsList);
                setTotalPages(response.totalPages);
                setTotalResult(response.totalItems);
            }
        } catch (err) {
            errorHandler.handleError(err);
        }
        setLoading(false);
    };

    const reRenderData = async () => {
        setLoading(true);
        try {
            const body = buildListFilter(0, size);
            let response = await leadsService.getFilteredList(body);
            if (response.status < 400) {
                response = JSON.parse(response.body);
                const leadsList = response.leadsList.map(parseLeadsData);
                setLeads(leadsList);
                setTotalResult(response.totalItems);
                setTotalPages(response.totalPages);
                setPage(response.currentPage + 1);
            }
        } catch (err) {
            errorHandler.handleError(err);
        }
        setLoading(false);
    };

    const handleStatusChange = async (value, id) => {
        try {
            setCursor('wait');

            const bodyObj = {
                id,
                status: value
            };

            let response = await leadsService.updateStatus(bodyObj);

            if (response.status < 400) {
                response = JSON.parse(response.body);

                const updatedLead = parseLeadsData(response);

                setLeads(prev =>
                    prev.map(item =>
                        item.id === updatedLead.id
                            ? updatedLead
                            : item
                    )
                );

                setSelectedLead(updatedLead);
            }
        } catch (err) {
            errorHandler.handleError(err);
        }

        setCursor('auto');
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'newLeads':
                return {
                    className: 'status-new',
                    icon: <Sparkles size={13} />,
                    label: 'New Lead'
                };

            case 'contacted':
                return {
                    className: 'status-hot',
                    icon: <TrendingUp size={13} />,
                    label: 'Contacted'
                };

            case 'active':
                return {
                    className: 'status-warm',
                    icon: <Clock size={13} />,
                    label: 'Active'
                };

            case 'followUp':
                return {
                    className: 'status-info',
                    icon: <PhoneCall size={13} />,
                    label: 'Follow Up'
                };

            case 'onHold':
                return {
                    className: 'status-muted',
                    icon: <PauseCircle size={13} />,
                    label: 'On Hold'
                };

            case 'onboarded':
                return {
                    className: 'status-success',
                    icon: <CheckCircle size={13} />,
                    label: 'Onboarded'
                };

            case 'closed':
                return {
                    className: 'status-danger',
                    icon: <XCircle size={13} />,
                    label: 'Closed'
                };

            case 'lost':
                return {
                    className: 'status-danger-soft',
                    icon: <Ban size={13} />,
                    label: 'Lost'
                };

            default:
                return {
                    className: 'status-default',
                    icon: null,
                    label: status || 'Unknown'
                };
        }
    };

    const getLeadCounts = () => {
        const counts = {
            all: totalResult,
            newLeads: 0,
            active: 0,
            followUp: 0,
            onboarded: 0
        };

        leads.forEach(lead => {
            if (counts[lead.status] !== undefined) {
                counts[lead.status]++;
            }
        });

        return counts;
    };

    const leadCounts = getLeadCounts();

    const formatDate = timestamp => {
        if (!timestamp) return '-';

        const date = moment(new Date(timestamp));
        const currentYear = moment().year();

        return date.year() !== currentYear
            ? date.format('MMM DD, YYYY')
            : date.format('MMM DD, hh:mm A');
    };

    const handleExport = async ({
        selectedOptions,
        splitBy,
        exportType
    }) => {
        const bodyObj = {
            exportType,
            splitBy,
            filterRequest: {
                leadsFilter: {
                    projectIdList: [currentProjectId]
                },
                options: selectedOptions.map(o =>
                    JSON.stringify(o)
                )
            }
        };

        return await leadsService.exportReport(bodyObj);
    };

    const reloadData = () => {
        setSearchQuery('');
        setPage(1);
    };

    const handlePaginationClick = (event, value) => {
        setPage(value);
    };

    const handleOpenLead = lead => {
        setSelectedLead(lead);
        setOpenLeadView(true);
    };

    const handleCloseLeadView = () => {
        setSelectedLead(null);
        setOpenLeadView(false);
    };

    const getItemName = row => (
        <div
            className={`${className}__lead`}
            onClick={() => handleOpenLead(row)}
        >
            <div className={`${className}__lead__avatar`}>
                <UserAvatar
                    name={row?.name}
                    size="small"
                />
            </div>

            <div className={`${className}__lead__content`}>
                <div className={`${className}__lead__name`}>
                    {row?.name || 'Unnamed lead'}
                </div>

                <div className={`${className}__lead__meta`}>
                    {row?.companyName ||
                        row?.contactNumber ||
                        'No company'}
                </div>
            </div>
        </div>
    );

    const getItemAssignee = row => (
        <div className={`${className}__assignee`}>
            <FullUserAvatar
                profileId={row?.assigneeUserId}
                size="medium"
            />
        </div>
    );

    const getQuery = lead => (
        <Tooltip title={lead?.query || 'No notes'} arrow>
            <div className={`${className}__notes`}>
                {lead?.query || '—'}
            </div>
        </Tooltip>
    );

    const getCreateDate = lead => (
        <div className={`${className}__date`}>
            <span>
                {formatDate(lead?.createTimeStamp)}
            </span>
        </div>
    );

    const getItemStatus = row => {
        const status = getStatusBadge(row?.status);

        return (
            <Select
                className={`${className}__statusSelect`}
                disableUnderline
                variant="standard"
                IconComponent={()=> null}
                value={row?.status || ''}
                onChange={e =>
                    handleStatusChange(
                        e.target.value,
                        row?.id
                    )
                }
                renderValue={selected => {
                    const selectedStatus =
                        getStatusBadge(selected);

                    return (
                        <span
                            className={`${className}__status ${selectedStatus.className}`}
                        >
                            {selectedStatus.icon}
                            {selectedStatus.label}
                        </span>
                    );
                }}
            >
                {LEAD_STATUSES.map(item => {
                    const itemStatus =
                        getStatusBadge(item.key);

                    return (
                        <MenuItem
                            value={item.key}
                            key={item.key}
                            className={`${className}__statusOption`}
                        >
                            <span
                                className={`${className}__status ${itemStatus.className}`}
                            >
                                {itemStatus.icon}
                                {itemStatus.label}
                            </span>
                        </MenuItem>
                    );
                })}
            </Select>
        );
    };

    const emptyList = () => (
        <div className={`${className}__empty`}>
            <div className={`${className}__empty__icon`}>
                <Users size={26} />
            </div>

            <h3>No leads found</h3>

            <p>
                {searchQuery
                    ? 'Try changing your search or filters.'
                    : 'Create your first lead to get started.'}
            </p>

            {!searchQuery && (
                <button
                    className={`${className}__empty__button`}
                    onClick={() =>
                        setShowCreateModal(true)
                    }
                >
                    <Plus size={15} />
                    Add Lead
                </button>
            )}
        </div>
    );

    const footer = (
        <div className={`${className}__footer`}>
            <div className={`${className}__footer__info`}>
                Showing{' '}
                <strong>
                    {leads.length}
                </strong>{' '}
                of{' '}
                <strong>
                    {totalResult}
                </strong>{' '}
                leads
            </div>

            <Stack spacing={1}>
                <Pagination
                    count={totalPages}
                    page={page}
                    showFirstButton
                    showLastButton
                    onChange={handlePaginationClick}
                    size="small"
                    siblingCount={1}
                    className={`${className}__pagination`}
                />
            </Stack>
        </div>
    );

    return (
        <>
            <div
                className={className}
                style={{ cursor }}
            >
                {/* Header */}
                <header className={`${className}__header`}>
                    <div className={`${className}__header__left`}>
                        <div className={`${className}__header__icon`}>
                            <Users size={19} />
                        </div>

                        <div>
                            <h1>
                                Leads
                            </h1>

                            <p>
                                Manage and track your sales pipeline
                            </p>
                        </div>
                    </div>

                    <div className={`${className}__header__actions`}>
                        <div
                            className={`${className}__search`}
                        >
                            <Search size={17} />

                            <input
                                type="text"
                                placeholder="Search leads..."
                                value={searchQuery}
                                onChange={e =>
                                    setSearchQuery(
                                        e.target.value
                                    )
                                }
                            />

                            {searchQuery && (
                                <button
                                    onClick={() =>
                                        setSearchQuery('')
                                    }
                                >
                                    ×
                                </button>
                            )}
                        </div>

                        <Tooltip title="Refresh" arrow>
                            <button
                                className={`${className}__iconButton`}
                                onClick={reloadData}
                            >
                                <RefreshCw
                                    size={16}
                                    className={
                                        loading
                                            ? 'is-loading'
                                            : ''
                                    }
                                />
                            </button>
                        </Tooltip>

                        <LeadsFilters
                            currentProjectId={
                                currentProjectId
                            }
                            pageNumber={page}
                            pageSize={size}
                            searchQuery={
                                debouncedSearch
                            }
                            whatsappPhoneNumberList={whatsappPhoneNumberList}
                            onApply={filters => {
                                setLeadFilter({
                                    leadsFilter: {
                                        statusList:
                                            filters
                                                .leadsFilter
                                                .statusList,
                                        sortBy:
                                            filters
                                                .leadsFilter
                                                .sortBy,
                                        sortAsc:
                                            filters
                                                .leadsFilter
                                                .sortAsc,
                                        sourceList: filters.leadsFilter.sourceList,
                                        sourceTargetIdList: filters.leadsFilter.sourceTargetIdList,
                                        createTimeStampFrom:
                                            filters
                                                .leadsFilter
                                                .createTimeStampFrom,
                                        createTimeStampTo:
                                            filters
                                                .leadsFilter
                                                .createTimeStampTo
                                    }
                                });

                                setPage(1);
                            }}
                        />

                        <button
                            className={`${className}__secondaryButton`}
                            onClick={() =>
                                setShowLeadsDownloadForm(
                                    true
                                )
                            }
                        >
                            <FileDown size={15} />
                            <span>Export</span>
                        </button>

                        <button
                            className={`${className}__primaryButton`}
                            onClick={() =>
                                setShowCreateModal(true)
                            }
                        >
                            <Plus size={16} />
                            Add Lead
                        </button>
                    </div>
                </header>

                {/* KPI strip */}
                <section className={`${className}__stats`}>
                    <div className={`${className}__stat active`}>
                        <div className={`${className}__stat__icon`}>
                            <Users size={17} />
                        </div>

                        <div>
                            <span>Total Leads</span>
                            <strong>{totalResult}</strong>
                        </div>
                    </div>

                    <div className={`${className}__stat`}>
                        <div className={`${className}__stat__icon blue`}>
                            <Sparkles size={17} />
                        </div>

                        <div>
                            <span>New</span>
                            <strong>
                                {leadCounts.newLeads}
                            </strong>
                        </div>
                    </div>

                    <div className={`${className}__stat`}>
                        <div className={`${className}__stat__icon orange`}>
                            <PhoneCall size={17} />
                        </div>

                        <div>
                            <span>Follow Up</span>
                            <strong>
                                {leadCounts.followUp}
                            </strong>
                        </div>
                    </div>

                    <div className={`${className}__stat`}>
                        <div className={`${className}__stat__icon green`}>
                            <CheckCircle size={17} />
                        </div>

                        <div>
                            <span>Onboarded</span>
                            <strong>
                                {leadCounts.onboarded}
                            </strong>
                        </div>
                    </div>

                    <div className={`${className}__stats__caption`}>
                        {loading && 'Updating leads...'}
                    </div>
                </section>
                <section
                    className={`${className}__tableCard`}
                >
                    {loading && (
                        <div
                            className={`${className}__loadingBar`}
                        />
                    )}

                    <DataTable
                        value={leads}
                        ref={leadsListRef}
                        scrollable
                        footer={footer}
                        scrollHeight="flex"
                        style={{
                            height:
                                'calc(100dvh - 200)'
                        }}
                        tableStyle={{
                            minWidth: '850px'
                        }}
                        columnResizeMode="expand"
                        emptyMessage={emptyList}
                        resizableColumns
                        dataKey="id"
                        rowClassName={() =>
                            `${className}__tableRow`
                        }
                    >
                        <Column
                            field="name"
                            header="Lead"
                            style={{
                                minWidth: '250px'
                            }}
                            body={getItemName}
                        />

                        <Column
                            field="assignee"
                            header="Assignee"
                            style={{
                                minWidth: '130px'
                            }}
                            body={getItemAssignee}
                        />

                        <Column
                            field="status"
                            header="Status"
                            style={{
                                minWidth: '150px'
                            }}
                            body={getItemStatus}
                        />

                        <Column
                            field="query"
                            header="Notes"
                            style={{
                                minWidth: '260px'
                            }}
                            body={getQuery}
                        />

                        <Column
                            field="createDate"
                            header="Created"
                            style={{
                                minWidth: '170px'
                            }}
                            body={getCreateDate}
                        />

                        <Column
                            header=""
                            body={row => (
                                <Tooltip
                                    title="View lead"
                                    arrow
                                >
                                    <button
                                        className={`${className}__viewButton`}
                                        onClick={() =>
                                            handleOpenLead(
                                                row
                                            )
                                        }
                                    >
                                        <Eye size={15} />
                                    </button>
                                </Tooltip>
                            )}
                            style={{
                                width: '60px'
                            }}
                        />
                    </DataTable>
                </section>
            </div>

            {/* Create */}
            {showCreateModal && (
                <CreateLeadsForm
                    setLeads={setLeads}
                    setSelectedLead={
                        setSelectedLead
                    }
                    onClose={() =>
                        setShowCreateModal(false)
                    }
                />
            )}

            {/* Lead View */}
            {openLeadView && (
                <LeadsView
                    open={openLeadView}
                    onClose={handleCloseLeadView}
                    id={selectedLead?.id}
                    reloadData={reRenderData}
                />
            )}

            {/* Export */}
            <ExportDownloadForm
                open={showLeadsDownloadForm}
                closePopup={() =>
                    setShowLeadsDownloadForm(false)
                }
                defaultOptions={
                    DEFAULT_DOWNLOAD_OPTIONS
                }
                splitLabels={{
                    '': 'None',
                    status: 'Status',
                    priority: 'Priority',
                    source: 'Source'
                }}
                onExport={handleExport}
            />
        </>
    );
}

export default LeadsPage;
